import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon, FunnelIcon, PencilSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import 'styles/AgroDataEdit.css'
import { useTranslation } from 'react-i18next'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import AgroDataEditModal from 'components/modals/AgroDataEditModal'
import PageLoadingState from 'components/common/PageLoadingState'
import ModalComponent from 'components/common/ModalComponent'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'
import { exportDataEditExcel, exportDataEditPdf, filterRowsBySearch } from 'utils/dataEditExport'

const exportColumns = [
  { key: 'rowCode', label: 'Barcode Number' },
  { key: 'date', label: 'Date' },
  { key: 'activityName', label: 'Activity Name' },
  { key: 'shared', label: 'Shared' },
  { key: 'workerName', label: 'Worker' },
  { key: 'blockName', label: 'Block' },
  { key: 'location', label: 'Location' },
  { key: 'units', label: 'Units' },
  { key: 'completionPercentage', label: '% Complete' }
]

export default function AgroDataEdit () {
  const hasFetched = useRef(false)
  const currentFilter = useRef(null)
  const { user } = useAuth()
  const { t } = useTranslation()
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [state, setState] = useState({
    agroData: [],
    searchTerm: '',
    showEditModal: false,
    selectedAgroData: null,
    isBulkEdit: false,
    loading: true,
    showDeleteConfirm: false,
    pendingDelete: null
  })

  const getAgroData = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/data-edit/agro-data/${user.farmId}?${buildDashboardDateRangeQuery(dateRange)}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, agroData: response.data, loading: false }))
    } catch (error) {
      console.error('Error:', error)
      setState(s => ({ ...s, loading: false }))
    }
  }

  const openSingleEditModal = (row) => {
    setState(s => ({ ...s, showEditModal: true, selectedAgroData: row, isBulkEdit: false }))
  }

  const openBulkEditModal = () => {
    setState(s => ({ ...s, showEditModal: true, selectedAgroData: null, isBulkEdit: true }))
  }

  const closeModal = () => {
    setState(s => ({ ...s, showEditModal: false, selectedAgroData: null, isBulkEdit: false }))
  }

  const handleSaveAgroData = async (formData) => {
    try {
      if (state.isBulkEdit) {
        const ids = selectedRows.map(r => r.agroDataId)
        await makeRequest({
          method: 'PATCH',
          url: '/management/data-edit/agro-data/bulk',
          data: { ids, ...formData },
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      } else {
        await makeRequest({
          method: 'PATCH',
          url: `/management/data-edit/agro-data/${state.selectedAgroData.agroDataId}`,
          data: formData,
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      }
      showAlert({ text: t('mgmt.agroDataEdit.updated'), icon: 'success' })
      closeModal()
      await getAgroData()
    } catch (error) {
      showAlert({ text: t('mgmt.agroDataEdit.updateFailed'), icon: 'error' })
      console.error('Error updating agro data:', error)
    }
  }

  const handleDeleteAgroData = (row) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: row }))
  }

  const confirmDeleteAgroData = async () => {
    const row = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/data-edit/agro-data/${row.agroDataId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: t('mgmt.agroDataEdit.deleted'), icon: 'success' })
      await getAgroData()
    } catch (error) {
      showAlert({ text: t('mgmt.agroDataEdit.deleteFailed'), icon: 'error' })
      console.error('Error deleting agro data:', error)
    }
  }

  const filterAgroData = filterRowsBySearch(state.agroData, state.searchTerm)

  useEffect(() => {
    if (!user?.farmId) return
    const nextFilter = JSON.stringify(dateRange)
    if (hasFetched.current && currentFilter.current === nextFilter) return
    hasFetched.current = true
    currentFilter.current = nextFilter
    getAgroData()
  }, [user?.farmId, dateRange])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.agroDataEdit.loading')} rows={8} />
  }

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>{getDateRangeSummary(dateRange)}</span>
          </div>
        </div>

        <div className='flex items-center gap-4 mb-6'>
          {selectedRows.length > 0 && (
            <ButtonComponent className='flex items-center gap-2' onClick={openBulkEditModal}>
              <PencilSquareIcon className='w-5 h-5' />
              <span>{t('mgmt.dataEdit.editSelection', { count: selectedRows.length })}</span>
            </ButtonComponent>
          )}
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditExcel({ rows: filterAgroData, columns: exportColumns, fileName: `agro-data-${currentDate}`, sheetName: 'Agro Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{t('common.exportExcel')}</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditPdf({ rows: filterAgroData, columns: exportColumns, fileName: `agro-data-${currentDate}`, title: 'Agro Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{t('mgmt.common.exportPdf')}</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder={t('mgmt.dataEdit.search')}
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <ButtonComponent className='flex items-center gap-2' onClick={() => setShowFilters(true)}>
          <FunnelIcon className='w-5 h-5' />
          <span>{t('dashboard.advancedFilters')}</span>
        </ButtonComponent>
      </div>

      <div className='data-table'>
        {state.agroData.length !== 0 && (
          <TableComponent
            tableData={{
              hasCheckbox: true,
              columns: [
                { key: 'rowCode', label: 'Barcode Number' },
                { key: 'date', labelKey: 'mgmt.dataEdit.cols.date', cell: (value) => parseDate(value) },
                { key: 'activityName', label: 'Activity Name' },
                { key: 'shared', labelKey: 'mgmt.dataEdit.cols.shared', cell: (value) => value ? t('mgmt.common.yes') : t('mgmt.common.no') },
                { key: 'workerName', label: 'Worker' },
                { key: 'blockName', label: 'Block' },
                { key: 'location', label: 'Location' },
                { key: 'units', labelKey: 'mgmt.dataEdit.cols.units', cell: (value) => value ?? '-' },
                { key: 'completionPercentage', labelKey: 'mgmt.dataEdit.cols.complete', cell: (value) => value != null ? `${value}%` : '-' },
                { key: 'actions', labelKey: 'mgmt.dataEdit.cols.manage' }
              ],
              rows: filterAgroData
            }}
            handleEdit={openSingleEditModal}
            handleDelete={handleDeleteAgroData}
            onSelectionChange={setSelectedRows}
            defaultSortColumn='rowCode'
          />
        )}
      </div>

      <AgroDataEditModal
        isOpen={state.showEditModal}
        handleClose={closeModal}
        handleSave={handleSaveAgroData}
        selectedAgroData={state.selectedAgroData}
        isBulk={state.isBulkEdit}
        bulkCount={selectedRows.length}
        farmId={user.farmId}
      />
      <DateRangeFilterModal isOpen={showFilters} dateRange={dateRange} sectors={filterOptions.sectors} farmingBlocks={filterOptions.farmingBlocks} groups={filterOptions.groups} activities={filterOptions.activities} maxDate={currentDate} onClose={() => setShowFilters(false)} onApply={(nextRange) => { setDateRange(nextRange); setShowFilters(false) }} />

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.agroDataEdit.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {state.pendingDelete?.date
              ? t('mgmt.agroDataEdit.removeConfirmWithDate', { name: state.pendingDelete?.workerName ?? '', date: state.pendingDelete.date })
              : t('mgmt.agroDataEdit.removeConfirm', { name: state.pendingDelete?.workerName ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDeleteAgroData}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </div>
  )
}
